import BaseComponent from "@js/components/base_component.jsx";
import EuiDnsStatusPanel from "@js/components/eui/dns_status_panel.jsx";
import EuiLinkedConfigurationRow from "@js/components/eui/linked_configuration_row.jsx";
import EuiUrlDomainCertStatus from "@js/components/eui/url_domain_cert_status.jsx";

class EuiSendingDomainsForm extends BaseComponent {

  static defaultProps = {
    editing: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      domain: props.sending_domain.domain || "",
      shared_tracking_domain_id: props.sending_domain.shared_tracking_domain_id,
      dkim_mode: props.sending_domain.dkim_mode || "automatic",
      sending_enabled: props.sending_domain.sending_enabled,
    };
  }

  render() {
    const sd = this.props.sending_domain;
    const isNew = !sd.persisted;
    const editing = this.props.editing;
    const usesSharedTracking = !!this.state.shared_tracking_domain_id;
    // DKIM mode can only change at creation, so read the live state only when
    // it's actually editable; otherwise trust the persisted value.
    const dkimMode = isNew ? this.state.dkim_mode : sd.dkim_mode;

    return (
      <div>

        <ErrorBanner entity={ sd } />

        <Panel heading="sending_domain_settings">
          <div className="form-horizontal">

            { isNew ? (
              <InputField
                object={ sd }
                type="sending_domain"
                field="domain"
                editing={ true }
                onChange={ (e) => this.setState({ domain: e.target.value }) }
                helpRawHtml={ I18n.t("simple_form.hints.sending_domain.domain_html") }
              />
            ) : (
              this._readOnlyField("sending_domain.domain", sd.domain, I18n.t("simple_form.hints.sending_domain.domain_html"))
            )}

            { isNew ? (
              <SelectField
                field="sending_domain.cname_suffix_domain"
                blank={ null }
                value={ sd.cname_suffix_domain }
                options={ this._cnameSuffixDomainOptions() }
                editing={ true }
                error={ sd.errors ? sd.errors.cname_suffix_domain : null }
                helpRawHtml={ I18n.t("simple_form.hints.sending_domain.cname_suffix_domain_html") }
              />
            ) : (
              this._readOnlyField("sending_domain.cname_suffix_domain", sd.cname_suffix_domain, I18n.t("simple_form.hints.sending_domain.cname_suffix_domain_html"))
            )}

            <CheckboxField
              name="sending_domain[sending_enabled]"
              label={ I18n.t("simple_form.labels.sending_domain.sending_enabled") }
              checked={ this.state.sending_enabled }
              editing={ editing }
              onChange={ (checked) => this.setState({ sending_enabled: checked }) }
              helpRawHtml={ this._help(I18n.t("simple_form.hints.sending_domain.sending_enabled_html")) }
            />

            { !sd.dns_verified && sd.persisted && (
              <div className="form-group">
                <div className="col-sm-offset-4 col-sm-8">
                  <span className="text-danger">{ I18n.t("phrases.dns_not_verified_cannot_send") }</span>
                </div>
              </div>
            )}

          </div>
        </Panel>

        <Panel heading="tracking_settings">
          <div className="form-horizontal">

            { (isNew || usesSharedTracking) && (
              isNew ? (
                <SelectField
                  field="sending_domain.shared_tracking_domain_id"
                  blank="shared_tracking_domain"
                  value={ sd.shared_tracking_domain_id }
                  options={ this._sharedTrackingDomainOptions() }
                  editing={ true }
                  error={ sd.errors ? sd.errors.shared_tracking_domain_id : null }
                  onChange={ this._sharedTrackingDomainChanged }
                  helpRawHtml={ I18n.t("simple_form.hints.sending_domain.shared_tracking_domain_id_html") }
                />
              ) : (
                this._readOnlyField(
                  "sending_domain.shared_tracking_domain_id",
                  sd.shared_tracking_domain_name,
                  I18n.t("simple_form.hints.sending_domain.shared_tracking_domain_id_html")
                )
              )
            )}

            { !usesSharedTracking && (
              isNew ? (
                <InputField
                  object={ sd }
                  type="sending_domain"
                  field="tracking_subdomain"
                  editing={ true }
                  inputAddonSuffix={ "." + (this.state.domain || sd.domain || "") }
                  helpRawHtml={ I18n.t("simple_form.hints.sending_domain.tracking_subdomain_html") }
                />
              ) : (
                this._readOnlyField(
                  "sending_domain.tracking_subdomain",
                  sd.tracking_domain,
                  I18n.t("simple_form.hints.sending_domain.tracking_subdomain_html")
                )
              )
            )}

            { !editing && !usesSharedTracking && (
              <EuiLinkedConfigurationRow
                incoming_email_domain_id={ sd.incoming_email_domain_id }
                url_domain_id={ sd.url_domain_id }
              />
            )}

            { !editing && (
              <EuiUrlDomainCertStatus tls_cert={ sd.url_domain_https_status } />
            )}

            <SelectField
              field="sending_domain.track_open"
              blank="use_mail_class_setting"
              blankShowText={ I18n.t("blank_options.use_mail_class_setting") }
              value={ this._trackSettingValue(sd.track_open) }
              options={ this._trackSettingOptions() }
              editing={ editing }
              helpRawHtml={ this._help(I18n.t("simple_form.hints.sending_domain.track_open_html")) }
            />

            <SelectField
              field="sending_domain.track_click"
              blank="use_mail_class_setting"
              blankShowText={ I18n.t("blank_options.use_mail_class_setting") }
              value={ this._trackSettingValue(sd.track_click) }
              options={ this._trackSettingOptions() }
              editing={ editing }
              helpRawHtml={ this._help(I18n.t("simple_form.hints.sending_domain.track_click_html")) }
            />

          </div>
        </Panel>

        { this.props.library.system_configuration.greenarrow_internal_routes && (
          <Panel heading="routing_settings">
            <div className="form-horizontal">

              <SelectField
                field="sending_domain.route_id"
                blank="route"
                value={ sd.route_id }
                options={ this._routeOptions() }
                editing={ editing }
                error={ sd.errors ? sd.errors.route_id : null }
                showHrefFn={ (id) => Routes.eui_route_path(id) }
                helpRawHtml={ this._help(I18n.t("simple_form.hints.sending_domain.route_id_html")) }
              />

            </div>
          </Panel>
        )}

        <Panel heading="dkim_settings">
          <div className="form-horizontal">

            { isNew ? (
              <SelectField
                field="sending_domain.dkim_mode"
                blank={ null }
                value={ sd.dkim_mode }
                options={ this._dkimModeOptions() }
                editing={ true }
                error={ sd.errors ? sd.errors.dkim_mode : null }
                onChange={ this._dkimModeChanged }
                helpRawHtml={ I18n.t("simple_form.hints.sending_domain.dkim_mode_html") }
              />
            ) : (
              this._readOnlyField("sending_domain.dkim_mode", I18n.t("sending_domain_dkim_modes." + sd.dkim_mode), I18n.t("simple_form.hints.sending_domain.dkim_mode_html"))
            )}

            { isNew ? (
              <InputField
                object={ sd }
                type="sending_domain"
                field="dkim_selector1"
                editing={ true }
                helpRawHtml={ I18n.t("simple_form.hints.sending_domain.dkim_selector1_html") }
              />
            ) : (
              this._readOnlyField(
                "sending_domain.dkim_selector1",
                sd.dkim_key1_id
                  ? <a href={ Routes.eui_dkim_key_path(sd.dkim_key1_id) }>{ sd.dkim_selector1 }</a>
                  : sd.dkim_selector1,
                I18n.t("simple_form.hints.sending_domain.dkim_selector1_html")
              )
            )}

            { dkimMode !== "txt_single" && (
              isNew ? (
                <InputField
                  object={ sd }
                  type="sending_domain"
                  field="dkim_selector2"
                  editing={ true }
                  helpRawHtml={ I18n.t("simple_form.hints.sending_domain.dkim_selector2_html") }
                />
              ) : (
                this._readOnlyField(
                  "sending_domain.dkim_selector2",
                  sd.dkim_key2_id
                    ? <a href={ Routes.eui_dkim_key_path(sd.dkim_key2_id) }>{ sd.dkim_selector2 }</a>
                    : sd.dkim_selector2,
                  I18n.t("simple_form.hints.sending_domain.dkim_selector2_html")
                )
              )
            )}

            { isNew ? (
              <SelectField
                field="sending_domain.dkim_key_length"
                blank={ null }
                value={ sd.dkim_key_length }
                options={ this._dkimKeyLengthOptions() }
                editing={ true }
                error={ sd.errors ? sd.errors.dkim_key_length : null }
                helpRawHtml={ I18n.t("simple_form.hints.sending_domain.dkim_key_length_html") }
              />
            ) : (
              this._readOnlyField("sending_domain.dkim_key_length", sd.dkim_key_length, I18n.t("simple_form.hints.sending_domain.dkim_key_length_html"))
            )}

          </div>
        </Panel>

        { !editing && (
          <EuiDnsStatusPanel
            domain={ sd.domain }
            dns_verified={ sd.dns_verified }
            dns_verified_time_pretty={ sd.dns_verified_time_pretty }
            dns_regression={ sd.dns_regression }
            dns_regression_time_pretty={ sd.dns_regression_time_pretty }
            wanted_dns_records={ sd.wanted_dns_records }
            unwanted_dns_records={ sd.unwanted_dns_records }
          />
        )}

      </div>
    );
  }

  // Help text only appears on the edit / new pages, not the read-only view
  // page, where it is visual noise. Only the view page (the Show wrapper) passes
  // editing={false} explicitly; on new/edit the flag is true or undefined, so
  // hide help solely when it is explicitly false.
  _help(html) {
    return this.props.editing === false ? null : html;
  }

  // Renders a label/value row for a field that can't be changed after creation.
  // The field's help (if any) hangs off the clickable label rather than a "?"
  // icon, and only in edit mode.
  _readOnlyField(labelKey, content, helpHtml) {
    const isEmpty = content === null || content === undefined || content === "";

    return (
      <FieldContainer labelText={ I18n.t("simple_form.labels." + labelKey) } helpRawHtml={ this._help(helpHtml) }>
        { isEmpty ? <span>&mdash;</span> : content }
      </FieldContainer>
    );
  }

  _cnameSuffixDomainOptions() {
    return (this.props.allowed_cname_suffix_domains || []).map(function(d) {
      return { value: d, label: d };
    });
  }

  _sharedTrackingDomainOptions() {
    return (this.props.shared_tracking_domains || []).map(function(std) {
      return { value: std.id, label: std.name };
    });
  }

  _routeOptions() {
    return (this.props.routes || []).map(function(r) {
      return { value: r.id, label: r.name };
    });
  }

  _dkimModeOptions() {
    return [
      { value: "automatic", label: I18n.t("sending_domain_dkim_modes.automatic") },
      { value: "txt_single", label: I18n.t("sending_domain_dkim_modes.txt_single") },
      { value: "txt_multiple", label: I18n.t("sending_domain_dkim_modes.txt_multiple") },
    ];
  }

  _dkimKeyLengthOptions() {
    return [
      { value: 1024, label: "1024" },
      { value: 2048, label: "2048" },
    ];
  }

  // track_open / track_click are tri-state. The select submits "true"/"false"
  // or "" (the blank "Use Mail Class setting" option, which the server stores as
  // NULL to inherit the mail class). Map the persisted boolean/null to that.
  _trackSettingValue(value) {
    if (value === true) return "true";
    if (value === false) return "false";
    return "";
  }

  _trackSettingOptions() {
    return [
      { value: "true", label: I18n.t("sending_domain_track_settings.enabled") },
      { value: "false", label: I18n.t("sending_domain_track_settings.disabled") },
    ];
  }

  _sharedTrackingDomainChanged(e) {
    this.setState({ shared_tracking_domain_id: e.val || null });
  }

  _dkimModeChanged(e) {
    this.setState({ dkim_mode: e.val });
  }

}

export default EuiSendingDomainsForm;
